import React, { useState } from 'react';
import styled from '@emotion/styled';

export interface GraphFilters {
  searchQuery: string;
  showOrphans: boolean;
  conceptTypes: string[];
  minFrequency: number;
}

export interface GraphForces {
  centerForce: number;
  repelForce: number;
  linkForce: number;
  linkDistance: number;
}

export interface GraphDisplay {
  showArrows: boolean;
  textFade: number;
  nodeSize: number;
  linkThickness: number;
}

interface GraphControlsProps {
  filters: GraphFilters;
  forces: GraphForces;
  display: GraphDisplay;
  onFiltersChange: (filters: GraphFilters) => void;
  onForcesChange: (forces: GraphForces) => void;
  onDisplayChange: (display: GraphDisplay) => void;
}

const Container = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  width: 280px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  
  @media (max-width: 768px) {
    position: fixed;
    top: auto;
    bottom: 20px;
    right: 20px;
    left: 20px;
    width: auto;
    max-height: 60vh;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.2);
  }
`;

const Section = styled.div<{ collapsed?: boolean }>`
  border-bottom: 1px solid var(--color-border);
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionHeader = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: var(--color-surface);
  transition: background 0.2s;
  
  &:hover {
    background: var(--color-surface-hover);
  }
  
  @media (max-width: 768px) {
    padding: 18px;
    border-bottom: 1px solid rgba(var(--color-border-rgb), 0.2);
  }
`;

const SectionContent = styled.div<{ collapsed?: boolean }>`
  padding: ${props => props.collapsed ? '0 16px' : '0 16px 16px'};
  max-height: ${props => props.collapsed ? '0' : '500px'};
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: ${props => props.collapsed ? '0' : '1'};
  
  @media (max-width: 768px) {
    padding: ${props => props.collapsed ? '0 18px' : '0 18px 18px'};
  }
`;

const CollapseIcon = styled.span<{ collapsed?: boolean }>`
  transition: transform 0.2s ease;
  transform: ${props => props.collapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Control = styled.div`
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-background);
  color: var(--color-text);
  font-size: 13px;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const Slider = styled.input`
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: var(--color-border);
  outline: none;
  opacity: 0.7;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
    border: none;
  }
`;

const SliderValue = styled.span`
  font-size: 11px;
  color: var(--color-text-secondary);
  margin-left: 8px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--color-text);
  cursor: pointer;
`;

const ConceptTypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const ConceptTypeButton = styled.button<{ active: boolean }>`
  padding: 6px 10px;
  border: 1px solid ${props => props.active ? 'var(--color-primary)' : 'var(--color-border)'};
  border-radius: var(--radius-sm);
  background: ${props => props.active ? 'var(--color-primary-light)' : 'var(--color-background)'};
  color: ${props => props.active ? 'var(--color-primary)' : 'var(--color-text)'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: var(--color-primary);
  }
`;

const HelpTooltip = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: var(--color-text-secondary);
  color: var(--color-surface);
  border-radius: 50%;
  font-size: 10px;
  cursor: help;
  
  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--color-background);
    color: var(--color-text);
    padding: 8px 12px;
    border-radius: var(--radius-md);
    font-size: 12px;
    white-space: nowrap;
    box-shadow: var(--shadow-lg);
    z-index: 1000;
    border: 1px solid var(--color-border);
  }
`;

const CONCEPT_TYPES = [
  'THEORY',
  'CANON',
  'SHIP',
  'CONTROVERSY',
  'MEME',
  'CHARACTER',
  'EVIDENCE',
  'DEBATE'
];

const HELP_TEXT = {
  filters: 'Control which nodes and edges are visible in the graph',
  display: 'Adjust the visual appearance of nodes and connections',  
  forces: 'Fine-tune the physics simulation that positions nodes',
  searchQuery: 'Find specific nodes by name',
  showOrphans: 'Show nodes that have no connections to other nodes',
  conceptTypes: 'Filter by the type of concept or topic',
  minFrequency: 'Only show nodes mentioned at least this many times',
  showArrows: 'Display directional arrows on connections',
  textFade: 'Hide text labels when zoomed out beyond this level',
  nodeSize: 'Scale the size of all nodes',
  linkThickness: 'Adjust the thickness of connection lines',
  centerForce: 'How strongly nodes are pulled toward the center',
  repelForce: 'How strongly nodes push away from each other',
  linkForce: 'How strongly connected nodes are pulled together',
  linkDistance: 'The ideal distance between connected nodes'
};

export const GraphControls: React.FC<GraphControlsProps> = ({
  filters,
  forces,
  display,
  onFiltersChange,
  onForcesChange,
  onDisplayChange
}) => {
  const [isExpanded, setIsExpanded] = useState({
    filters: true,
    display: false,
    forces: false
  });

  const toggleSection = (section: keyof typeof isExpanded) => {
    setIsExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleConceptTypeToggle = (type: string) => {
    const newTypes = filters.conceptTypes.includes(type)
      ? filters.conceptTypes.filter(t => t !== type)
      : [...filters.conceptTypes, type];
    
    onFiltersChange({ ...filters, conceptTypes: newTypes });
  };

  return (
    <Container>
      <Section>
        <SectionHeader onClick={() => toggleSection('filters')}>
          <SectionTitle>
            Filters
            <HelpTooltip data-tooltip={HELP_TEXT.filters}>?</HelpTooltip>
          </SectionTitle>
          <CollapseIcon collapsed={!isExpanded.filters}>▼</CollapseIcon>
        </SectionHeader>
        <SectionContent collapsed={!isExpanded.filters}>
          <Control>
            <Label>Search</Label>
            <Input
              type="text"
              placeholder="Search nodes..."
              value={filters.searchQuery}
              onChange={(e) => onFiltersChange({ ...filters, searchQuery: e.target.value })}
            />
          </Control>
        
        <Control>
          <CheckboxLabel>
            <Checkbox
              type="checkbox"
              checked={filters.showOrphans}
              onChange={(e) => onFiltersChange({ ...filters, showOrphans: e.target.checked })}
            />
            Show orphan nodes
          </CheckboxLabel>
        </Control>
        
        <Control>
          <Label>Concept Types</Label>
          <ConceptTypeGrid>
            {CONCEPT_TYPES.map(type => (
              <ConceptTypeButton
                key={type}
                active={filters.conceptTypes.includes(type)}
                onClick={() => handleConceptTypeToggle(type)}
              >
                {type}
              </ConceptTypeButton>
            ))}
          </ConceptTypeGrid>
        </Control>
        
        <Control>
          <Label>
            Min Frequency: {filters.minFrequency}
          </Label>
          <Slider
            type="range"
            min="1"
            max="10"
            value={filters.minFrequency}
            onChange={(e) => onFiltersChange({ ...filters, minFrequency: parseInt(e.target.value) })}
          />
        </Control>
        </SectionContent>
      </Section>
      
      <Section>
        <SectionHeader onClick={() => toggleSection('display')}>
          <SectionTitle>
            Display
            <HelpTooltip data-tooltip={HELP_TEXT.display}>?</HelpTooltip>
          </SectionTitle>
          <CollapseIcon collapsed={!isExpanded.display}>▼</CollapseIcon>
        </SectionHeader>
        <SectionContent collapsed={!isExpanded.display}>
        
        <Control>
          <CheckboxLabel>
            <Checkbox
              type="checkbox"
              checked={display.showArrows}
              onChange={(e) => onDisplayChange({ ...display, showArrows: e.target.checked })}
            />
            Show arrows
          </CheckboxLabel>
        </Control>
        
        <Control>
          <Label>
            Text fade threshold
            <SliderValue>{display.textFade}</SliderValue>
          </Label>
          <Slider
            type="range"
            min="0"
            max="5"
            step="0.1"
            value={display.textFade}
            onChange={(e) => onDisplayChange({ ...display, textFade: parseFloat(e.target.value) })}
          />
        </Control>
        
        <Control>
          <Label>
            Node size
            <SliderValue>{display.nodeSize}x</SliderValue>
          </Label>
          <Slider
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={display.nodeSize}
            onChange={(e) => onDisplayChange({ ...display, nodeSize: parseFloat(e.target.value) })}
          />
        </Control>
        
        <Control>
          <Label>
            Link thickness
            <SliderValue>{display.linkThickness}x</SliderValue>
          </Label>
          <Slider
            type="range"
            min="0.5"
            max="3"
            step="0.1"
            value={display.linkThickness}
            onChange={(e) => onDisplayChange({ ...display, linkThickness: parseFloat(e.target.value) })}
          />
        </Control>
        </SectionContent>
      </Section>
      
      <Section>
        <SectionHeader onClick={() => toggleSection('forces')}>
          <SectionTitle>
            Forces
            <HelpTooltip data-tooltip={HELP_TEXT.forces}>?</HelpTooltip>
          </SectionTitle>
          <CollapseIcon collapsed={!isExpanded.forces}>▼</CollapseIcon>
        </SectionHeader>
        <SectionContent collapsed={!isExpanded.forces}>
        
        <Control>
          <Label>
            Center force
            <SliderValue>{forces.centerForce}</SliderValue>
          </Label>
          <Slider
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={forces.centerForce}
            onChange={(e) => onForcesChange({ ...forces, centerForce: parseFloat(e.target.value) })}
          />
        </Control>
        
        <Control>
          <Label>
            Repel force
            <SliderValue>{forces.repelForce}</SliderValue>
          </Label>
          <Slider
            type="range"
            min="-1000"
            max="0"
            step="50"
            value={forces.repelForce}
            onChange={(e) => onForcesChange({ ...forces, repelForce: parseFloat(e.target.value) })}
          />
        </Control>
        
        <Control>
          <Label>
            Link force
            <SliderValue>{forces.linkForce}</SliderValue>
          </Label>
          <Slider
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={forces.linkForce}
            onChange={(e) => onForcesChange({ ...forces, linkForce: parseFloat(e.target.value) })}
          />
        </Control>
        
        <Control>
          <Label>
            Link distance
            <SliderValue>{forces.linkDistance}px</SliderValue>
          </Label>
          <Slider
            type="range"
            min="10"
            max="300"
            step="10"
            value={forces.linkDistance}
            onChange={(e) => onForcesChange({ ...forces, linkDistance: parseFloat(e.target.value) })}
          />
        </Control>
        </SectionContent>
      </Section>
    </Container>
  );
};